import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../components/Header';
import GlobeMap from '../components/GlobeMap';
import AudioEqualizer from '../components/AudioEqualizer';
import { useRadio } from '../contexts/RadioContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { fetchAllStations } from '../services/radioService';
import { LocationOn, MusicNote, Headphones, PlayArrow, KeyboardArrowDown, Close, Radio, Pause } from '@mui/icons-material';
import GettingStartedModal from '../components/GettingStartedModal';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import BreadcrumbSchema from '../components/BreadcrumbSchema';
import '../styles/Home.css';
import '../styles/PlayerEffects.css';

const AnimatedListenerCount = ({ count }) => {
  const [displayCount, setDisplayCount] = useState(count);
  const [prevCount, setPrevCount] = useState(count);
  
  useEffect(() => {
    const interval = setInterval(() => {
      // Random fluctuation between -5% and +5% of the original count
      const fluctuation = Math.floor(count * (Math.random() * 0.1 - 0.05));
      const newCount = count + fluctuation;
      
      setPrevCount(displayCount);
      setDisplayCount(newCount);
    }, 2000); // Update every 2 seconds
    
    return () => clearInterval(interval);
  }, [count, displayCount]);
  
  // Format the numbers with commas and convert to strings
  const currentStr = displayCount.toLocaleString();
  const prevStr = prevCount.toLocaleString();
  
  // Function to determine if a digit is increasing, decreasing or staying the same
  const getDigitDirection = (index) => {
    // Remove commas for comparison
    const current = currentStr.replace(/,/g, '');
    const prev = prevStr.replace(/,/g, '');
    
    // If the strings are of different length, we can't directly compare digits
    if (current.length !== prev.length) {
      return current.length > prev.length ? 'increasing' : 'decreasing';
    }
    
    // For digits comparison, we need to check the actual digit after accounting for commas
    const currentDigit = current[index] || '';
    const prevDigit = prev[index] || '';
    
    if (currentDigit > prevDigit) return 'increasing';
    if (currentDigit < prevDigit) return 'decreasing';
    return 'same';
  };
  
  return (
    <span className="animated-count">
      {currentStr.split('').map((digit, index) => (
        <span key={index} className={`digit ${digit === ',' ? 'comma' : getDigitDirection(index)}`}>
          {digit}
        </span>
      ))}
      <span className="listeners-text"> listeners</span>
    </span>
  );
};

const Home = () => {
  const location = useLocation();
  const { getText } = useLanguage();
  const { stations, loading, error, currentStation, isPlaying, playStation } = useRadio();
  const { currentUser } = useAuth();
  const [showGettingStarted, setShowGettingStarted] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);

  // Handle scroll button visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setShowScrollButton(scrollPosition > 300); // Show button after 300px scroll
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check if user has seen the getting started guide
  useEffect(() => {
    const checkUserStatus = async () => {
      if (!currentUser) {
        // For non-logged in users, check localStorage
        const hasSeenGuide = localStorage.getItem('hasSeenGettingStarted');
        if (!hasSeenGuide) {
          setShowGettingStarted(true);
        }
        return;
      }

      // For logged in users, check Firestore
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (!userData.hasSeenGettingStarted) {
            setShowGettingStarted(true);
          }
        }
      } catch (error) {
        console.error('Error checking user status:', error);
      }
    };

    checkUserStatus();
  }, [currentUser]);

  const handleGettingStartedClose = async () => {
    setShowGettingStarted(false);
    
    if (currentUser) {
      // Update Firestore for logged in users
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, {
          hasSeenGettingStarted: true
        });
      } catch (error) {
        console.error('Error updating user status:', error);
      }
    } else {
      // Update localStorage for non-logged in users
      localStorage.setItem('hasSeenGettingStarted', 'true');
    }
  };

  const scrollToContent = () => {
    window.scrollTo({
      top: document.querySelector('.featured-stations').offsetTop - 100,
      behavior: 'smooth'
    });
  };

  const handlePlayClick = (e, station) => {
    e.stopPropagation(); // Prevent card click event
    playStation(station);
  };

  return (
    <div className="home-page">
      <SEO 
        title="GlobeTune - World Radio Stations | Listen Live"
        description="Discover and listen to thousands of radio stations from around the world with GlobeTune. High-quality streaming, no signup required."
        canonicalUrl="/"
      />
      <StructuredData type="WebSite" />
      <StructuredData type="WebApplication" 
        data={{
          "potentialAction": {
            "@type": "ListenAction",
            "target": "https://globetune.co.uk"
          }
        }} 
      />
      <StructuredData type="Organization" />
      <BreadcrumbSchema items={[{ name: 'Home', path: '/' }]} />
      
      <Header title={getText('explore')} />
      
      <div className="globe-section">
        <GlobeMap stations={stations} />
      </div>
      
      {/* Show getting started modal for first-time visitors */}
      {showGettingStarted && (
        <GettingStartedModal onClose={handleGettingStartedClose} />
      )}
      
      {loading ? (
        <div className="loading-indicator">{getText('loadingStations')}</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="featured-stations">
          <h2>{getText('featuredStations')}</h2>
          <div className="stations-grid">
            {stations.slice(0, 8).map(station => (
              <div key={station.id} className="station-card">
                <div className="station-image">
                  {currentStation?.id === station.id && isPlaying ? (
                    <AudioEqualizer isPlaying={isPlaying} />
                  ) : (
                    <Radio />
                  )}
                </div>
                
                <div className="station-details">
                  <h3 className="station-name">{station.name}</h3>
                  <div className="station-location">
                    <LocationOn fontSize="small" style={{ marginRight: '5px', flexShrink: 0 }} />
                    <span>{station.country}{station.city ? `, ${station.city}` : ''}</span>
                  </div>
                  <div className="station-genres">
                    <MusicNote fontSize="small" style={{ marginRight: '5px', flexShrink: 0 }} />
                    <span>{station.genres?.join(' • ') || getText('various')}</span>
                  </div>
                  <div className="station-listeners">
                    <Headphones fontSize="small" style={{ marginRight: '5px', flexShrink: 0 }} />
                    <AnimatedListenerCount count={station.listeners || 0} />
                  </div>
                </div>

                <button 
                  className={`station-play-button ${currentStation?.id === station.id && isPlaying ? 'playing' : ''}`}
                  onClick={(e) => handlePlayClick(e, station)}
                  aria-label={currentStation?.id === station.id && isPlaying ? 'Pause' : 'Play'}
                >
                  {currentStation?.id === station.id && isPlaying ? <Pause /> : <PlayArrow />}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scroll down button */}
      {showScrollButton && (
        <button className="scroll-down-button" onClick={scrollToContent}>
          <KeyboardArrowDown />
        </button>
      )}
    </div>
  );
};

export default Home;